import React from 'react';
import {View} from 'react-native';
import {flow, isEmpty, join, trim} from 'lodash/fp';

import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import ScreenSection from '/component/layout/ScreenSection';
import {OrderDetails} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import testProperties from '/util/testing/testProperties';

import {styles as sectionStyles} from '../Section.style';

import {styles} from './AddressesSection.style';

const getTrimmedValue = (value = '') => trim(value);
const hasValue = (value = '') => !isEmpty(getTrimmedValue(value));

interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
}

interface AddressesSectionProps {
  tt: (key: string, value: string) => string;
  billingAddress: Address;
  shippingAddress: Address;
}

export const AddressesSection: React.FC<AddressesSectionProps> = ({
  tt = withTranslation.ttDefaultProp,
  billingAddress = {},
  shippingAddress = {},
}: AddressesSectionProps) => {
  const getCityStateValue = (city?: string, state?: string) =>
    flow([join(', '), trim])(
      [getTrimmedValue(city || ''), getTrimmedValue(state || '')].filter(
        value => !isEmpty(value),
      ),
    );

  const renderAddressLine = (value: string, testID: string) =>
    hasValue(value) ? (
      <Text testID={testID} accessible={true}>
        {value}
      </Text>
    ) : null;

  const buildAddressAccessibilityLabel = (
    title: string,
    {line1, line2, city, state, postalCode, countryCode}: Address = {},
  ) => {
    const parts = [
      title,
      getTrimmedValue(line1 || ''),
      getTrimmedValue(line2 || ''),
      getCityStateValue(city, state),
      getTrimmedValue(postalCode || ''),
      getTrimmedValue(countryCode || ''),
    ].filter(hasValue);

    return parts.join(', ');
  };

  const renderAddress = (
    title: any,
    {line1, line2, city, state, postalCode, countryCode}: any = {},
    headingTestID: string,
    line1TestID: string,
    line2TestID: string,
    cityStateTestID: string,
    postalCodeTestID: string,
    countryCodeTestID: string,
  ) => (
    <View style={styles.address}>
      <Text variant="button" testID={headingTestID} accessible={true}>
        {title}
      </Text>
      {renderAddressLine(getTrimmedValue(line1 || ''), line1TestID)}
      {renderAddressLine(getTrimmedValue(line2 || ''), line2TestID)}
      {renderAddressLine(getCityStateValue(city, state), cityStateTestID)}
      {renderAddressLine(getTrimmedValue(postalCode || ''), postalCodeTestID)}
      {renderAddressLine(getTrimmedValue(countryCode || ''), countryCodeTestID)}
    </View>
  );

  return (
    <ScreenSection
      style={{...sectionStyles, content: styles.base}}
      title={generateComponentCallback({
        component: (
          <Gutter>
            <Text
              variant="subheading"
              emphasis="low"
              testID={OrderDetails.addressesSectionHeading}
              accessible={true}
              accessibilityRole="header">
              {tt('heading', 'Shipping and Payment')}
            </Text>
          </Gutter>
        ),
      })}>
      <Gutter>
        <View
          accessible={false}
          accessibilityLabel={buildAddressAccessibilityLabel(
            tt('address.billing.heading', 'Billing Address'),
            billingAddress,
          )}
          {...testProperties(OrderDetails.billingAddress)}>
          {renderAddress(
            tt('address.billing.heading', 'Billing Address'),
            billingAddress,
            OrderDetails.billingAddressHeading,
            OrderDetails.billingAddressLine1,
            OrderDetails.billingAddressLine2,
            OrderDetails.billingAddressCityState,
            OrderDetails.billingAddressPostalCode,
            OrderDetails.billingAddressCountryCode,
          )}
        </View>
        <View style={styles.divider} />
        <View
          accessible={false}
          accessibilityLabel={buildAddressAccessibilityLabel(
            tt('address.shipping.heading', 'Shipping Address'),
            shippingAddress,
          )}
          {...testProperties(OrderDetails.shippingAddress)}>
          {renderAddress(
            tt('address.shipping.heading', 'Shipping Address'),
            shippingAddress,
            OrderDetails.shippingAddressHeading,
            OrderDetails.shippingAddressLine1,
            OrderDetails.shippingAddressLine2,
            OrderDetails.shippingAddressCityState,
            OrderDetails.shippingAddressPostalCode,
            OrderDetails.shippingAddressCountryCode,
          )}
        </View>
      </Gutter>
    </ScreenSection>
  );
};

export default withTranslation('screen.orderDetail.section.addresses')(
  AddressesSection,
);
